import { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useSnackbar } from "notistack";
import { editBaselineData, getBaselineData } from "../operations/ProjectOperations";
import { Box, TextField, Tooltip } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { Snackbar } from "@mui/material";
import Alert from "@mui/material/Alert";
import { CircularProgress } from "@mui/material";
import Task from "../model/Task";
import SaveIcon from "@mui/icons-material/Save";
import ProjectStore from "../model/ProjectStore";
import { NumericFormat } from "react-number-format";

const generateGridRow = (
  task,
  plannedProgressEntry,
  index,
  readOnly,
  setIsTaskPackage
) => {
  const period = plannedProgressEntry.period;
  const actualProgressEntry = task.getActualProgressForPeriod(period);
  const expectedProgress = plannedProgressEntry.expectedProgress;
  const expectedCost = plannedProgressEntry.expectedCost;

  const actualProgress = task.actualProgress.filter((ap) => ap.period > 0);
  let cumulativeProgress = 0.0;
  let cumulativeCost = 0.0;
  for (let i = 1; i <= index + 1; i++) {
    if (actualProgress.length >= i) {
      cumulativeProgress += !isNaN(parseFloat(actualProgress[i - 1].progress))
        ? parseFloat(actualProgress[i - 1].progress)
        : 0;
      cumulativeCost += !isNaN(parseFloat(actualProgress[i - 1].actualCost))
        ? parseFloat(actualProgress[i - 1].actualCost) //parseInt(actualProgress[i - 1].actualCost)
        : 0;
    }
  }
  setIsTaskPackage(readOnly);

  return {
    period: period,
    expectedProgress: expectedProgress,
    progress: actualProgressEntry.progress,
    cumulativeProgress: cumulativeProgress,
    expectedCost: expectedCost,
    cost: actualProgressEntry.actualCost,
    cumulativeCost: cumulativeCost,
    note: actualProgressEntry.note,
  };
};

const formatCost = (value) => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(value);
};

const formatProgress = (value) => {
  return `${parseFloat(value).toFixed(2)}%`;
};

function NumericInputCell({ id, value, field, api, hasFocus, isProgress }) {
  const [localValue, setLocalValue] = useState(value || "");

  const handleValueChange = (values) => {
    const sanitizedValue =
      values.value === "-" ||
        values.value === "."
        ? ""
        : values.value;
    setLocalValue(sanitizedValue);
    api.setEditCellValue(
      { id, field, value: sanitizedValue },
      { debounceMs: 200 }
    );
  };

  return (
    <NumericFormat
      value={localValue}
      id={`${id}_${field}`}
      key={`${id}_${field}`}
      onValueChange={handleValueChange}
      fullWidth
      thousandSeparator
      prefix={isProgress ? undefined : "$"}
      suffix={isProgress ? "%" : undefined}
      customInput={TextField}
      InputProps={{
        disableUnderline: true,
        autoFocus: hasFocus,
        style: { fontSize: "14px", padding: "16px" },
      }}
    />
  );
}

function DialogEditBaselineData({ open, selectedTask, rowId, readOnly, onClose }) {
  const [data, setData] = useState([]);
  const [isTaskPackage, setIsTaskPackage] = useState(true);
  const [preBaselineTask, setPreBaselineTask] = useState(null);
  const [loadingPreBaselineTask, setloadingPreBaselineTask] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingSuccess, setSavingSuccess] = useState(false);
  const [baselineNumber, setBaselineNumber] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const apiRef = useGridApiRef();

  const close = (event, reason) => {
    if (reason !== 'backdropClick') {
      setloadingPreBaselineTask(false);
      onClose();
    }
  };

  useEffect(() => {
    if (open === true) {
      setPreBaselineTask(null);
      setData([]);

      getBaselineData(
        selectedTask.project,
        selectedTask.id,
        rowId,
        setloadingPreBaselineTask,
        enqueueSnackbar,
        (returnedTask) => {
          let task = Task.fromJS(null, returnedTask, returnedTask.parent);
          setPreBaselineTask(task);
          setBaselineNumber(rowId);
          setData(
            task.plannedProgress
              .filter((pp) => pp.period > 0)
              .map((entry, index) =>
                generateGridRow(
                  task,
                  entry,
                  index,
                  readOnly,
                  setIsTaskPackage
                )
              )
          );
        }
      )
    }
  }, [open, selectedTask, rowId, readOnly, enqueueSnackbar]);

  const columns = [
    {
      field: "id",
      headerName: "Period",
      type: "number",
      editable: false,
      width: 125,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "uneditable";
        } else {
          return "uneditablePackg";
        }
      },
    },
    {
      field: "expectedProgress",
      headerName: "Exp Progress",
      type: "number",
      editable: !isTaskPackage,
      width: 175,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editableExp";
        } else {
          return "uneditableExp";
        }
      },
      renderEditCell: (params) => <NumericInputCell {...params} isProgress={true} />,
      valueFormatter: (params) => formatProgress(params.value),
    },
    {
      field: "progress",
      headerName: "Progress",
      type: "number",
      editable: !isTaskPackage,
      width: 150,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editable";
        } else {
          return "uneditablePackg";
        }
      },
      renderEditCell: (params) => <NumericInputCell {...params} isProgress={true} />,
      valueFormatter: (params) => formatProgress(params.value),
    },
    {
      field: "cumulativeProgress",
      headerName: "Cum Progress",
      editable: false,
      width: 170,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "uneditable";
        } else {
          return "uneditablePackg";
        }
      },
      valueFormatter: (params) => formatProgress(params.value),
    },
    {
      field: "expectedCost",
      headerName: "Exp Cost",
      type: "number",
      editable: !isTaskPackage,
      width: 150,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editableExp";
        } else {
          return "uneditableExp";
        }
      },
      renderEditCell: (params) => <NumericInputCell {...params} isProgress={false} />,
      valueFormatter: (params) => formatCost(params.value),
    },
    {
      field: "cost",
      headerName: "Cost",
      type: "number",
      editable: !isTaskPackage,
      width: 125,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editable";
        } else {
          return "uneditablePackg";
        }
      },
      renderEditCell: (params) => <NumericInputCell {...params} isProgress={false} />,
      valueFormatter: (params) => formatCost(params.value),
    },
    {
      field: "cumulativeCost",
      headerName: "Cum Cost",
      editable: false,
      width: 150,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "uneditable";
        } else {
          return "uneditablePackg";
        }
      },
      valueFormatter: (params) => formatCost(params.value),
    },
    {
      field: "note",
      headerName: "Note",
      editable: !isTaskPackage,
      width: 155,
      align: "center",
      headerAlign: "center",
      cellClassName: () => {
        if (!isTaskPackage) {
          return "editable";
        } else {
          return "uneditablePackg";
        }
      },
      renderCell: (params) => (
        <Tooltip title={params.value ?? " "}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
  ];

  const rows = data.map((row) => ({
    id: row.period,
    expectedProgress: row.expectedProgress,
    progress: row.progress,
    cumulativeProgress: row.cumulativeProgress,
    expectedCost: row.expectedCost,
    cost: row.cost,
    cumulativeCost: row.cumulativeCost,
    note: row.note
  }));

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setloadingPreBaselineTask(false);
    setSaving(false);
    setSavingSuccess(false);
  };

  const handleSubmit = () => {
    if (baselineNumber !== rowId || preBaselineTask.id !== selectedTask.id) {
      enqueueSnackbar(`Currently loaded baseline data does not correspond to the currently
        selected task or baseline number. Please try again.`, { variant: "error" })
    } else {
      editBaselineData(
        selectedTask.project,
        preBaselineTask,
        baselineNumber,
        setSaving,
        enqueueSnackbar,
        (result) => {
          setSavingSuccess(true);
          let project = ProjectStore.fromJS(result);
          selectedTask.project.currentHash = project.currentHash;
          selectedTask.project.baselines = project.baselines;
          selectedTask.project.preBaselines = null; // trigger refetching of preBaselines
        }
      )
    }
    close();
  };

  const handleOnCellEditCommit = (params) => {
    const actualProgressEntry = preBaselineTask.getActualProgressForPeriod(params.id);
    const plannedProgressEntry = preBaselineTask.getPlannedProgressForPeriod(params.id);

    if (params.field === "progress") {
      actualProgressEntry.progress = params.value;
    } else if (params.field === "expectedProgress") {
      plannedProgressEntry.expectedProgress = params.value;
    } else if (params.field === "cost") {
      actualProgressEntry.actualCost = params.value;
    } else if (params.field === "expectedCost") {
      plannedProgressEntry.expectedCost = params.value;
    } else {
      actualProgressEntry.note = params.value;
    }

    setData(
      preBaselineTask.plannedProgress
        .filter((pp) => pp.period > 0)
        .map((entry, index) =>
          generateGridRow(
            preBaselineTask,
            entry,
            index,
            readOnly,
            setIsTaskPackage
          )
        )
    ); // Updating array data, based on the change to the cell

    apiRef.current.updateRows(
      data.map((row) => ({
        id: row.period,
        expectedProgress: row.expectedProgress,
        progress: row.progress,
        cumulativeProgress: row.cumulativeProgress,
        expectedCost: row.expectedCost,
        cost: row.cost,
        cumulativeCost: row.cumulativeCost,
        note: row.note,
      }))
    ); // Commiting all the updates, based on the change made to the cell
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={close}
        aria-labelledby="form-dialog-title"
        scroll="paper"
        fullWidth maxWidth="lg"
      >
        <DialogTitle id="form-dialog-title">Edit Baseline Data</DialogTitle>
        <DialogContent dividers={true}>
          <Box
            sx={{
              height: 650,
              width: 1225,
              m: 'auto',
              "& .editable": {
                backgroundColor: "rgba(225, 255, 255, 0)",
                color: "#000000",
              },
              "& .editableExp": {
                backgroundColor: "rgba(225, 255, 255, 0)",
                color: "#000000",
                fontStyle: "italic",
              },
              "& .uneditable": {
                backgroundColor: "rgba(225, 255, 255, 0)",
                color: "#000000",
              },
              "& .uneditableExp": {
                backgroundColor: "rgba(225, 255, 255, 0)",
                color: "#808080",
                fontStyle: "italic",
              },
              "& .uneditablePackg": {
                backgroundColor: "rgba(225, 255, 255, 0)",
                color: "#808080",
              },
            }}
          >
            <DataGrid
              title="Baseline Data Grid"
              apiRef={apiRef}
              rows={rows}
              columns={columns}
              rowsPerPageOptions={[10]}
              pageSize={10}
              pagination
              onCellEditCommit={(params) => {
                handleOnCellEditCommit(params);
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button size="medium" variant="contained" onClick={close}>
            Cancel
          </Button>
          <Button
            size="medium"
            variant="contained"
            disabled={preBaselineTask === null || isTaskPackage}
            onClick={handleSubmit}
            startIcon={<SaveIcon />}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        title="Load PreBaselineData Message"
        open={loadingPreBaselineTask}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="info"
          sx={{ width: "100%" }}
        >
          <div>
            <CircularProgress color="inherit" />
            Please wait while the baseline information is being generated, this may
            take several minutes
          </div>
        </Alert>
      </Snackbar>
      <Snackbar
        title="Saving Started"
        open={saving}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="info"
          sx={{ width: "100%" }}
        >
          Saving...
        </Alert>
      </Snackbar>
      <Snackbar
        title="Saving Successul"
        open={savingSuccess}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          Baseline data successfully saved!
        </Alert>
      </Snackbar>
    </>
  );
}

export default DialogEditBaselineData;